import { useState, useMemo } from 'react';
import { Button, IconButton, Stack, Tooltip, Snackbar, Alert } from '@mui/material';
import { Add, Visibility, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { ExportMenu } from '../../../components/ExportMenu';
import { DataTable, type Column } from '../../../components/DataTable';
import { StatusBadge } from '../../../components/StatusBadge';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { ListFilters, matchFilters, EMPTY_FILTERS, type FilterValue } from '../../../components/ListFilters';
import { OrderFormDialog } from './OrderFormDialog';
import { OrderDetailDialog } from './OrderDetailDialog';
import { dealerOrdersApi, type DealerOrder, type CreateOrderInput, type OrderStatus } from '../../../api/dealerOrders.api';
import { dealersApi } from '../../../api/dealers.api';
import { clientMappingApi } from '../../../api/clientMapping.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';

const STATUSES: OrderStatus[] = ['draft', 'pending_approval', 'approved', 'in_manufacturing', 'manufactured', 'allocated', 'dispatched', 'delivered', 'rejected', 'cancelled'];
const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;

export function OrderListPage() {
  const qc = useQueryClient();
  const { hasPermission, user } = useAuth();
  const isAdmin = (user?.roles ?? []).some((r) => r === 'super_admin' || r === 'admin');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [filters, setFilters] = useState<FilterValue>({ ...EMPTY_FILTERS });
  const [formOpen, setFormOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<DealerOrder | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['dealer-orders', 'all'],
    queryFn: () => dealerOrdersApi.list({ limit: 500 }),
  });
  const dealers = useQuery({ queryKey: ['dealers', 'filter'], queryFn: () => dealersApi.list({ limit: 500 }), staleTime: 5 * 60 * 1000 });
  const dealerName = (id: number) => (dealers.data?.data.find((d) => d.id === id)?.dealerName) ?? `#${id}`;
  const invalidate = () => qc.invalidateQueries({ queryKey: ['dealer-orders'] });

  // When a Client is selected, scope orders to the dealers mapped to that client.
  const clientDealers = useQuery({
    queryKey: ['client-dealers-map', filters.clientId],
    queryFn: () => clientMappingApi.dealers(Number(filters.clientId)),
    enabled: Boolean(filters.clientId),
  });
  const clientDealerIds = filters.clientId ? new Set((clientDealers.data ?? []).map((d) => d.dealer_id)) : null;

  const filtered = useMemo(() => (data?.data ?? []).filter((o) =>
    matchFilters(filters, { text: o.orderCode, status: o.status, date: o.orderDate, dealerId: o.dealerId, regionId: o.regionId })
    && (!clientDealerIds || clientDealerIds.has(o.dealerId))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, filters, clientDealers.data]);
  const pageRows = filtered.slice((page - 1) * limit, page * limit);

  const createMut = useMutation({
    mutationFn: (input: CreateOrderInput) => dealerOrdersApi.create(input),
    onSuccess: (o) => { setFormOpen(false); invalidate(); setDetailId(o.id); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => dealerOrdersApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  const columns: Column<DealerOrder>[] = [
    { key: 'orderCode', label: 'Order', width: 150 },
    { key: 'dealerId', label: 'Dealer', render: (r) => dealerName(r.dealerId) },
    { key: 'grandTotal', label: 'Value', align: 'right', render: (r) => inr(r.grandTotal) },
    { key: 'orderDate', label: 'Order Date', render: (r) => r.orderDate ?? '—' },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <>
      <PageHeader
        title="Dealer Orders"
        subtitle="EVCPD order fulfilment: approval → manufacturing → allocation → dispatch → delivery."
        action={
          <Stack direction="row" spacing={1}>
            <ExportMenu dataset="dealer-orders" permission="evcpd_order.export" filters={{ status: filters.status }} />
            {hasPermission('dealer_order.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setFormError(null); setFormOpen(true); }}>New Order</Button>}
          </Stack>
        }
      />
      <ListFilters value={filters} onChange={(v) => { setFilters(v); setPage(1); }} statusOptions={STATUSES}
        show={['search', 'status', 'month', 'dateFrom', 'dateTo', 'region', 'client', 'dealer']} />
      <DataTable
        columns={columns}
        rows={pageRows}
        loading={isLoading}
        page={page}
        limit={limit}
        total={filtered.length}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          <>
            <Tooltip title="Open"><IconButton size="small" onClick={() => setDetailId(r.id)}><Visibility fontSize="small" /></IconButton></Tooltip>
            {hasPermission('dealer_order.delete') && (['draft', 'rejected', 'cancelled'].includes(r.status) || isAdmin) && (
              <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>
            )}
          </>
        )}
      />

      <OrderFormDialog
        open={formOpen}
        error={formError}
        onClose={() => setFormOpen(false)}
        onSubmit={async (input) => { setFormError(null); await createMut.mutateAsync(input); }}
      />
      <OrderDetailDialog orderId={detailId} onClose={() => setDetailId(null)} onChanged={invalidate} />
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete order"
        message={`Delete ${deleting?.orderCode}?`}
        confirmLabel="Delete"
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMut.mutate(deleting.id)}
      />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
